import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { mediaMSmMax } from 'helpers/breakpoints';
import { useBreakpoint } from 'components/BreakpointProvider';
import Image from 'next/image';

const Hero = () => {
  const breakpoints = useBreakpoint();
  const isMobile = breakpoints.xs || breakpoints.sm;
  const parallaxLeft = useRef(null);
  const parallaxRight = useRef(null);

  useEffect(() => {
    const scrollHandler = () => {
      const element = parallaxRight.current;
      if (element) {
        const offset = document.getElementById('__next').scrollTop;
        element.style.bottom = `${offset * 0.2 + (isMobile ? -50 : -100)}px`;
      }
    };
    document.getElementById('__next').addEventListener('scroll', scrollHandler);

    return () => document.getElementById('__next').removeEventListener('scroll', scrollHandler);
  }, [isMobile]);

  useEffect(() => {
    const scrollHandler = () => {
      if (isMobile) return;
      const element = parallaxLeft.current;
      if (element) {
        const offset = document.getElementById('__next').scrollTop;
        element.style.top = `${offset * 0.2 + (isMobile ? 0 : 40)}px`;
      }
    };
    document.getElementById('__next').addEventListener('scroll', scrollHandler);

    return () => document.getElementById('__next').removeEventListener('scroll', scrollHandler);
  }, [isMobile]);

  return (
    <MainContainer>
      <div className="hero-top">
        <div className="left-card" ref={parallaxLeft} style={{ top: isMobile ? 0 : 40 }}>
          <p className="title">WELCOME TO THE #1 BOAT RENTAL COMMUNITY</p>
          <h1>The sea’s the limit</h1>
          <p className="summary">
            Whether it’s a short sail around the bay, a day-long fishing excursion, or a week-long island-hopping
            charter, Boatsetter offers unique on-the-water experiences for everyone and every budget.
          </p>
        </div>
        <div className="middle-card">
          <Image
            src="/images/about/mid.jpeg"
            alt="Share a boat"
            height={isMobile ? 142 : 432}
            width={isMobile ? 220 : 742}
            style={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
          />
        </div>
        <div className="right-card" ref={parallaxRight} style={{ bottom: isMobile ? -50 : -100 }}>
          <Image
            src="/images/about/right.png"
            alt="Share a boat"
            height={isMobile ? 141 : 228}
            width={isMobile ? 209 : 332}
            style={{
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'cover',
            }}
          />
        </div>
      </div>
      <div className="hero-bottom">
        <h3>At Boatsetter, the only boundary we see is the horizon</h3>
        <p>
          Since our inception in 2012, we’ve made boating affordable and accessible for first-time boaters—500,000 and
          counting!—simply by providing an easy way to connect qualified renters, boat owners, and licensed captains.
          <br />
          <br />
          As a result, we’ve become the world’s leading boat rental community.{' '}
          <span>With more than 50,000 boats in over 700 locations worldwide</span>, chances are there&apos;s an
          available boat near you.
        </p>
      </div>
    </MainContainer>
  );
};

export default Hero;

const MainContainer = styled.div`
  padding: 100px 0 0 0;
  max-width: 1100px;
  margin: 0 auto;

  .hero-top {
    display: flex;
    justify-content: flex-end;
    position: relative;

    ${mediaMSmMax} {
      flex-direction: column;
      padding: 0 22px;
    }

    .left-card {
      padding: 60px 50px 60px 75px;
      overflow: hidden;
      width: 593px;
      height: 348px;
      border-radius: 16px;
      background: #ffffff;
      position: absolute;
      left: -75px;
      top: 40px;
      z-index: 5;

      ${mediaMSmMax} {
        width: 100%;
        position: relative;
        border-radius: 0;
        height: unset;
        left: 0;
        padding: 0;
        top: 0;
      }

      .title {
        font-style: normal;
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        letter-spacing: 0.05em;
        color: #17233c;

        ${mediaMSmMax} {
          font-size: 11px;
          margin-bottom: 0;
        }
      }

      h1 {
        font-style: normal;
        font-weight: bold;
        font-size: 50px;
        line-height: 55px;
        color: #17233c;

        ${mediaMSmMax} {
          font-size: 30px;
          line-height: 41px;
          margin-bottom: 8px;
        }
      }

      .summary {
        font-style: normal;
        font-weight: normal;
        font-size: 20px;
        line-height: 140%;
        color: #17233c;

        ${mediaMSmMax} {
          font-size: 16px;
          line-height: 24px;
        }
      }
    }

    .middle-card {
      border-radius: 20px;
      width: 742px;
      height: 432px;
      overflow: hidden;
      margin-right: 32px;

      ${mediaMSmMax} {
        width: 220px;
        height: 142px;
        border-radius: 10px;
        margin-right: 0;
        margin-top: 25px;
      }
    }

    .right-card {
      border-radius: 10px;
      width: 332px;
      height: 228px;
      overflow: hidden;
      position: absolute;
      z-index: 5;
      bottom: -100px;

      ${mediaMSmMax} {
        width: 209px;
        height: 141px;
        border-radius: 10px;
        right: -12px;
        bottom: -50px;
      }
    }
  }

  .hero-bottom {
    padding: 180px 0 80px 0;
    display: flex;
    justify-content: space-between;

    ${mediaMSmMax} {
      flex-direction: column;
      padding: 105px 0 25px 0;
      margin: 0 22px;
    }

    h3 {
      font-style: normal;
      font-weight: bold;
      font-size: 32px;
      line-height: 40px;
      color: #0a252f;
      max-width: 35%;

      ${mediaMSmMax} {
        font-size: 24px;
        line-height: 30px;
        max-width: 100%;
      }
    }

    p {
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;
      color: #000000;
      max-width: 60%;

      span {
        font-weight: 600;
      }

      ${mediaMSmMax} {
        max-width: 100%;
      }
    }
  }
`;
